import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart3, 
  PieChart, 
  LineChart, 
  Table, 
  Calendar,
  Filter,
  Plus,
  Trash2,
  Move,
  Eye,
  Settings,
  Download
} from 'lucide-react';

interface ReportField {
  id: string;
  name: string;
  type: 'text' | 'number' | 'date' | 'boolean';
  category: 'production' | 'quality' | 'resource' | 'schedule';
  description: string;
}

interface ReportWidget {
  id: string;
  type: 'chart' | 'table' | 'metric' | 'filter';
  title: string;
  fields: string[];
  chartType?: 'bar' | 'line' | 'pie' | 'area';
  position: { x: number; y: number; width: number; height: number };
  config: any;
}

interface ReportBuilderProps {
  initialReport?: {
    name: string;
    description: string;
    widgets: ReportWidget[];
  };
  onSave: (report: any) => void;
  onPreview: (report: any) => void;
}

// Mock available fields
const availableFields: ReportField[] = [
  // Production fields
  { id: 'production_job_id', name: 'Production Job ID', type: 'text', category: 'production', description: 'Unique identifier for production job' },
  { id: 'recipe_name', name: 'Recipe Name', type: 'text', category: 'production', description: 'Name of the recipe being produced' },
  { id: 'planned_quantity', name: 'Planned Quantity', type: 'number', category: 'production', description: 'Planned production quantity' },
  { id: 'actual_quantity', name: 'Actual Quantity', type: 'number', category: 'production', description: 'Actual production quantity' },
  { id: 'production_date', name: 'Production Date', type: 'date', category: 'production', description: 'Date of production' },
  { id: 'production_status', name: 'Production Status', type: 'text', category: 'production', description: 'Current status of production' },
  
  // Quality fields
  { id: 'quality_score', name: 'Quality Score', type: 'number', category: 'quality', description: 'Overall quality score' },
  { id: 'defect_count', name: 'Defect Count', type: 'number', category: 'quality', description: 'Number of defects found' },
  { id: 'inspection_date', name: 'Inspection Date', type: 'date', category: 'quality', description: 'Date of quality inspection' },
  { id: 'inspector_name', name: 'Inspector Name', type: 'text', category: 'quality', description: 'Name of quality inspector' },
  { id: 'passed_inspection', name: 'Passed Inspection', type: 'boolean', category: 'quality', description: 'Whether item passed inspection' },
  
  // Resource fields
  { id: 'resource_name', name: 'Resource Name', type: 'text', category: 'resource', description: 'Name of the resource' },
  { id: 'resource_type', name: 'Resource Type', type: 'text', category: 'resource', description: 'Type of resource (machine, operator, etc.)' },
  { id: 'utilization_rate', name: 'Utilization Rate', type: 'number', category: 'resource', description: 'Resource utilization percentage' },
  { id: 'downtime_hours', name: 'Downtime Hours', type: 'number', category: 'resource', description: 'Hours of downtime' },
  { id: 'maintenance_date', name: 'Maintenance Date', type: 'date', category: 'resource', description: 'Last maintenance date' },
  
  // Schedule fields
  { id: 'scheduled_start', name: 'Scheduled Start', type: 'date', category: 'schedule', description: 'Scheduled start date/time' },
  { id: 'scheduled_end', name: 'Scheduled End', type: 'date', category: 'schedule', description: 'Scheduled end date/time' },
  { id: 'actual_start', name: 'Actual Start', type: 'date', category: 'schedule', description: 'Actual start date/time' },
  { id: 'actual_end', name: 'Actual End', type: 'date', category: 'schedule', description: 'Actual end date/time' },
  { id: 'schedule_variance', name: 'Schedule Variance', type: 'number', category: 'schedule', description: 'Variance from scheduled time' },
];

export function ReportBuilder({ initialReport, onSave, onPreview }: ReportBuilderProps) {
  const [reportName, setReportName] = useState(initialReport?.name || '');
  const [reportDescription, setReportDescription] = useState(initialReport?.description || '');
  const [widgets, setWidgets] = useState<ReportWidget[]>(initialReport?.widgets || []);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [draggedField, setDraggedField] = useState<ReportField | null>(null);

  const filteredFields = availableFields.filter(field => 
    selectedCategory === 'all' || field.category === selectedCategory
  );

  const addWidget = (type: ReportWidget['type']) => {
    const newWidget: ReportWidget = {
      id: `widget_${Date.now()}`,
      type,
      title: `New ${type.charAt(0).toUpperCase() + type.slice(1)}`,
      fields: [],
      position: { x: 0, y: widgets.length * 200, width: 400, height: 200 },
      config: {},
    };

    if (type === 'chart') {
      newWidget.chartType = 'bar';
    }

    setWidgets([...widgets, newWidget]);
  };

  const updateWidget = (widgetId: string, updates: Partial<ReportWidget>) => {
    setWidgets(widgets.map(widget => 
      widget.id === widgetId ? { ...widget, ...updates } : widget
    ));
  };

  const removeWidget = (widgetId: string) => {
    setWidgets(widgets.filter(widget => widget.id !== widgetId));
  };

  const addFieldToWidget = (widgetId: string, fieldId: string) => {
    const widget = widgets.find(w => w.id === widgetId);
    if (widget && !widget.fields.includes(fieldId)) {
      updateWidget(widgetId, {
        fields: [...widget.fields, fieldId]
      });
    }
  };

  const removeFieldFromWidget = (widgetId: string, fieldId: string) => {
    const widget = widgets.find(w => w.id === widgetId);
    if (widget) {
      updateWidget(widgetId, {
        fields: widget.fields.filter(f => f !== fieldId)
      });
    }
  };

  const handleSave = () => {
    const report = {
      name: reportName,
      description: reportDescription,
      widgets,
      createdAt: new Date().toISOString(),
    };
    onSave(report);
  };

  const handlePreview = () => {
    const report = {
      name: reportName,
      description: reportDescription,
      widgets,
    };
    onPreview(report);
  };

  const getFieldById = (fieldId: string) => {
    return availableFields.find(f => f.id === fieldId);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'production': return 'bg-blue-100 text-blue-800';
      case 'quality': return 'bg-green-100 text-green-800';
      case 'resource': return 'bg-orange-100 text-orange-800';
      case 'schedule': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getWidgetIcon = (type: string) => {
    switch (type) {
      case 'chart': return <BarChart3 className="h-4 w-4" />;
      case 'table': return <Table className="h-4 w-4" />;
      case 'metric': return <BarChart3 className="h-4 w-4" />;
      case 'filter': return <Filter className="h-4 w-4" />;
      default: return <BarChart3 className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Report Header */}
      <Card>
        <CardHeader>
          <CardTitle>Report Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="reportName">Report Name</Label>
              <Input
                id="reportName"
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                placeholder="Enter report name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reportDescription">Description</Label>
              <Input
                id="reportDescription"
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                placeholder="Enter report description"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Field Library */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Available Fields</CardTitle>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="production">Production</SelectItem>
                  <SelectItem value="quality">Quality</SelectItem>
                  <SelectItem value="resource">Resource</SelectItem>
                  <SelectItem value="schedule">Schedule</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent className="space-y-2 max-h-96 overflow-y-auto">
              {filteredFields.map((field) => (
                <div
                  key={field.id}
                  draggable
                  onDragStart={() => setDraggedField(field)}
                  className="p-2 border rounded cursor-move hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{field.name}</span>
                    <Badge className={getCategoryColor(field.category)}>
                      {field.category}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{field.description}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Report Builder */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Report Layout</CardTitle>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => addWidget('chart')}>
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Chart
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => addWidget('table')}>
                    <Table className="h-4 w-4 mr-2" />
                    Table
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => addWidget('metric')}>
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Metric
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => addWidget('filter')}>
                    <Filter className="h-4 w-4 mr-2" />
                    Filter
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="min-h-96 border-2 border-dashed border-gray-200 rounded-lg p-4">
                {widgets.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                    <BarChart3 className="h-12 w-12 mb-4 opacity-50" />
                    <p>Drag fields here or add widgets to start building your report</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {widgets.map((widget) => (
                      <Card key={widget.id} className="relative">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {getWidgetIcon(widget.type)}
                              <Input
                                value={widget.title}
                                onChange={(e) => updateWidget(widget.id, { title: e.target.value })}
                                className="font-medium border-none p-0 h-auto"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              {widget.type === 'chart' && (
                                <Select
                                  value={widget.chartType}
                                  onValueChange={(value) => updateWidget(widget.id, { chartType: value as any })}
                                >
                                  <SelectTrigger className="w-32">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="bar">Bar Chart</SelectItem>
                                    <SelectItem value="line">Line Chart</SelectItem>
                                    <SelectItem value="pie">Pie Chart</SelectItem>
                                    <SelectItem value="area">Area Chart</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => removeWidget(widget.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div
                            className="min-h-24 border border-dashed border-gray-300 rounded p-2"
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                              e.preventDefault();
                              if (draggedField) {
                                addFieldToWidget(widget.id, draggedField.id);
                                setDraggedField(null);
                              }
                            }}
                          >
                            {widget.fields.length === 0 ? (
                              <div className="flex items-center justify-center h-16 text-gray-400 text-sm">
                                Drop fields here
                              </div>
                            ) : (
                              <div className="space-y-2">
                                <Label className="text-xs font-medium">Fields:</Label>
                                <div className="flex flex-wrap gap-2">
                                  {widget.fields.map((fieldId) => {
                                    const field = getFieldById(fieldId);
                                    return field ? (
                                      <Badge
                                        key={fieldId}
                                        variant="outline"
                                        className="flex items-center gap-1"
                                      >
                                        {field.name}
                                        <button
                                          onClick={() => removeFieldFromWidget(widget.id, fieldId)}
                                          className="ml-1 hover:text-red-600"
                                        >
                                          ×
                                        </button>
                                      </Badge>
                                    ) : null;
                                  })}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Widget Preview */}
                          <div className="mt-4 p-4 bg-gray-50 rounded">
                            <div className="text-xs text-gray-500 mb-2">Preview:</div>
                            {widget.type === 'chart' && (
                              <div className="h-32 bg-white border rounded flex items-center justify-center">
                                <div className="text-center text-gray-400">
                                  <BarChart3 className="h-8 w-8 mx-auto mb-2" />
                                  <p className="text-sm">{widget.chartType} Chart Preview</p>
                                </div>
                              </div>
                            )}
                            {widget.type === 'table' && (
                              <div className="bg-white border rounded">
                                <div className="grid grid-cols-3 gap-2 p-2 border-b bg-gray-50">
                                  {widget.fields.slice(0, 3).map((fieldId) => {
                                    const field = getFieldById(fieldId);
                                    return (
                                      <div key={fieldId} className="text-xs font-medium">
                                        {field?.name || 'Field'}
                                      </div>
                                    );
                                  })}
                                </div>
                                <div className="p-2 text-center text-gray-400 text-sm">
                                  Table data will appear here
                                </div>
                              </div>
                            )}
                            {widget.type === 'metric' && (
                              <div className="bg-white border rounded p-4 text-center">
                                <div className="text-2xl font-bold text-gray-400">123</div>
                                <div className="text-sm text-gray-500">Sample Metric</div>
                              </div>
                            )}
                            {widget.type === 'filter' && (
                              <div className="bg-white border rounded p-2">
                                <div className="text-sm text-gray-500">Filter controls will appear here</div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-4">
        <Button variant="outline" onClick={handlePreview}>
          <Eye className="h-4 w-4 mr-2" />
          Preview
        </Button>
        <Button onClick={handleSave}>
          <Download className="h-4 w-4 mr-2" />
          Save Report
        </Button>
      </div>
    </div>
  );
} 